/**
 * Nimikekirjasto-sivu — laskutuksen toistuvien rivien hallinta.
 *
 * - Lista: nimi, kuvaus, yksikkö, hinta, ALV%, käyttömäärä
 * - Lisää/muokkaa/poista
 * - Haku + kategoriasuodatus
 * - Tuonti CSV:stä (myöhemmin)
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus, Edit, Trash2, Search, Package, Loader2, ArrowLeft, Save,
} from "lucide-react";
import { toast } from "sonner";

const eur = (v) => new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR" })
  .format(Number(v || 0));

const newForm = () => ({
  name: "",
  description: "",
  unit: "kpl",
  unit_price: 0,
  vat_percent: 25.5,
  default_quantity: 1,
  category: "",
  code: "",
});

export default function InvoiceItemsCatalog() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(newForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = [];
      if (q) params.push(`q=${encodeURIComponent(q)}`);
      if (category) params.push(`category=${encodeURIComponent(category)}`);
      const [a, c] = await Promise.all([
        api.get("/invoice-items" + (params.length ? `?${params.join("&")}` : "")),
        api.get("/invoice-items/categories"),
      ]);
      setItems(a.data);
      setCategories(c.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  }, [q, category]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Nimike on pakollinen kenttä");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        unit: form.unit || "kpl",
        unit_price: parseFloat(form.unit_price) || 0,
        vat_percent: parseFloat(form.vat_percent) || 25.5,
        default_quantity: parseFloat(form.default_quantity) || 1,
        category: form.category || null,
        code: form.code || null,
      };
      if (form.id) {
        await api.put(`/invoice-items/${form.id}`, payload);
        toast.success("Nimike päivitetty");
      } else {
        await api.post("/invoice-items", payload);
        toast.success("Nimike lisätty");
      }
      setShowForm(false);
      setForm(newForm());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Poistetaanko nimike?")) return;
    try {
      await api.delete(`/invoice-items/${id}`);
      toast.success("Poistettu");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="invoice-items-catalog">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/invoicing")} className="text-white/60">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
            <Package className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Laskutus</p>
            <h1 className="text-3xl font-display gradient-text-luxe">Nimikekirjasto</h1>
            <p className="text-white/60 text-sm mt-1">
              Tallenna toistuvat laskurivit, valitse ne yhdellä klikkauksella uusille laskuille.
            </p>
          </div>
        </div>
        <Button
          onClick={() => { setForm(newForm()); setShowForm(true); }}
          className="btn-luxe inline-flex items-center gap-2"
          data-testid="item-add"
        >
          <Plus className="w-4 h-4" /> Uusi nimike
        </Button>
      </header>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Hae..."
            className="pl-10"
            data-testid="item-search"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
          data-testid="item-category-filter"
        >
          <option value="">Kaikki kategoriat</option>
          {categories.map((c) => (
            <option key={c.category} value={c.category}>{c.category} ({c.count})</option>
          ))}
        </select>
      </div>

      {/* List */}
      <div className="glass-card p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-white/40">
            <Loader2 className="w-5 h-5 animate-spin" /> Ladataan...
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-white/40">
            <Package className="w-12 h-12 mx-auto mb-3 text-white/20" />
            <p>Ei vielä nimikkeitä. Lisää ensimmäinen.</p>
          </div>
        ) : (
          <table className="w-full text-sm relative z-10">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
                <th className="py-3 px-4">Nimike</th>
                <th className="py-3 px-4">Kategoria</th>
                <th className="py-3 px-4">Yksikkö</th>
                <th className="py-3 px-4 text-right">Hinta</th>
                <th className="py-3 px-4 text-right">ALV</th>
                <th className="py-3 px-4 text-right">Käyttöä</th>
                <th className="py-3 px-4 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`item-row-${it.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium">{it.name}</p>
                      {it.code && <code className="text-[10px] bg-white/5 text-white/60 px-1.5 rounded">{it.code}</code>}
                    </div>
                    {it.description && <p className="text-[11px] text-white/40 mt-0.5">{it.description}</p>}
                  </td>
                  <td className="py-3 px-4">
                    {it.category ? (
                      <Badge className="text-[10px] bg-amber-500/15 text-amber-200 border border-amber-500/30">{it.category}</Badge>
                    ) : <span className="text-white/30">—</span>}
                  </td>
                  <td className="py-3 px-4 text-white/70">{it.unit}</td>
                  <td className="py-3 px-4 text-right text-amber-300 font-mono font-semibold">{eur(it.unit_price)}</td>
                  <td className="py-3 px-4 text-right text-violet-300">{it.vat_percent} %</td>
                  <td className="py-3 px-4 text-right text-white/50">{it.usage_count || 0}×</td>
                  <td className="py-3 px-4">
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="w-7 h-7"
                        onClick={() => { setForm({ ...it }); setShowForm(true); }}
                        data-testid={`item-edit-${it.id}`}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="w-7 h-7 text-red-300"
                        onClick={() => remove(it.id)}
                        data-testid={`item-delete-${it.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Form dialog */}
      <Dialog open={showForm} onOpenChange={(v) => !v && setShowForm(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{form.id ? "Muokkaa nimikettä" : "Uusi nimike"}</DialogTitle>
            <DialogDescription>
              Nimikkeitä voi valita laskua tehdessä yhdellä klikkauksella.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nimike *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Esim. Asiantuntijatyö, h"
                autoFocus
                data-testid="item-form-name"
              />
            </div>
            <div>
              <Label className="text-xs">Kuvaus laskulla</Label>
              <Input
                value={form.description || ""}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Tekstinpätkä joka tulee laskun riville"
                data-testid="item-form-description"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Yksikkö</Label>
                <Input
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  placeholder="kpl, h, kk, krt..."
                  data-testid="item-form-unit"
                />
              </div>
              <div>
                <Label className="text-xs">Yksikköhinta €</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.unit_price}
                  onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                  data-testid="item-form-price"
                />
              </div>
              <div>
                <Label className="text-xs">ALV %</Label>
                <select
                  value={form.vat_percent}
                  onChange={(e) => setForm({ ...form, vat_percent: parseFloat(e.target.value) })}
                  className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm h-9"
                  data-testid="item-form-vat"
                >
                  {[0, 10, 14, 24, 25.5].map((r) => <option key={r} value={r}>{r} %</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Oletusmäärä</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.default_quantity}
                  onChange={(e) => setForm({ ...form, default_quantity: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Kategoria</Label>
                <Input
                  value={form.category || ""}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="Palvelut, Tuotteet..."
                />
              </div>
              <div>
                <Label className="text-xs">Koodi</Label>
                <Input
                  value={form.code || ""}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="SKU-1234"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowForm(false)} className="flex-1">Peruuta</Button>
              <Button onClick={save} disabled={saving} className="btn-luxe flex-1" data-testid="item-form-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Tallenna
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
